/**
 * The durable outbox: commands that have been applied locally but not yet
 * accepted by the server.
 *
 * Every dispatch lands here **before** it is pushed, and leaves only when the
 * server has said what became of it. That ordering is what makes the app
 * offline-safe: closing the tab mid-edit, losing Wi-Fi in a cellar, or a socket
 * dying between `send` and `ack` all end the same way — the command is still on
 * disk and gets pushed again.
 *
 * Redelivery is therefore normal, not exceptional, which is why the server
 * dedupes on `commandId` and answers a repeat with `duplicate` rather than
 * applying it twice. The outbox is at-least-once; the server makes it
 * exactly-once.
 *
 * ## Ordering
 *
 * IndexedDB assigns the keys, monotonically, in append order. Replay order is
 * key order, so the sequence a user typed is the sequence the server sees and
 * the sequence a reload reconstructs.
 *
 * ## Ownership
 *
 * Entries record the user they belong to. One browser profile can hold rows for
 * more than one account (sign out, sign in as someone else, sign back), and
 * pushing another account's commands into this user's workspace would be a
 * data leak. {@link Outbox.load} filters; it never deletes what it filters.
 *
 * The owner is a **key**, not necessarily a user id. A workspace outbox is
 * keyed by the user's id, as it always was. A shared recipe's room has an
 * outbox of its own in the same store, keyed `` `${userId}:room:${recipeId}` ``
 * (PLAN-COLLAB C1), so a room's queue can be dropped whole when the room is
 * gone without touching the workspace's. The key is written to the row's
 * `owner` field exactly as the user id always was, so every row an existing
 * workspace outbox holds still reads.
 */

import type { Command } from '@beerwright/commands';
import { parseCommand } from '@beerwright/commands';
import type { AppendLogStore } from '@/store/idb';
import { openAppendLog } from '@/store/idb';

/** IndexedDB database the outbox shares with the workspace snapshot. */
export const OUTBOX_DATABASE = 'beerwright';

/** Object store holding the append-only log. */
export const OUTBOX_STORE = 'outbox';

/** What one row looks like on disk. `owner` is the outbox's key; see "Ownership". */
interface OutboxRecord {
  readonly owner: string;
  readonly command: unknown;
}

/**
 * The identity IndexedDB assigns a row, and the only handle that can delete it.
 *
 * Named rather than left as a bare `number` because the sync client has to
 * reason about *not having one yet*: a command is on screen before its write
 * has committed, and `OutboxKey | null` is how that half-durable state is
 * spelled there (see `sync-client.ts`).
 */
export type OutboxKey = number;

/** One pending command, with the key that will prune it. */
export interface OutboxEntry {
  readonly key: OutboxKey;
  readonly command: Command;
}

/**
 * One queue's pending commands: a user's workspace, or a room they are in.
 *
 * Deliberately not a cache: `load()` reads from disk, and the sync client holds
 * the in-memory copy. There is one writer per tab and the store is the only
 * durable truth, so there is nothing to invalidate.
 */
export class Outbox {
  readonly #store: AppendLogStore;
  readonly #owner: string;

  /**
   * @param key - Whose queue this is: the signed-in user's id for their
   *   workspace, or `` `${userId}:room:${recipeId}` `` for a room. Only rows
   *   written under this key are loaded.
   * @param store - Storage backend; tests pass an in-memory one.
   */
  constructor(key: string, store: AppendLogStore = openAppendLog(OUTBOX_DATABASE, OUTBOX_STORE)) {
    this.#owner = key;
    this.#store = store;
  }

  /** Append one command. Resolves with the entry key once the write has committed. */
  async append(command: Command): Promise<OutboxEntry> {
    const record: OutboxRecord = { owner: this.#owner, command };
    const key = await this.#store.append(record);
    return { key, command };
  }

  /**
   * Every pending command under this key, oldest first.
   *
   * A row that no longer parses as a command — written by a build that has
   * since removed that command type — is skipped rather than fatal, and its key
   * is returned for pruning so it cannot wedge the queue forever.
   */
  async load(): Promise<{ entries: readonly OutboxEntry[]; unreadable: readonly OutboxKey[] }> {
    const rows = await this.#store.all<OutboxRecord>();
    const entries: OutboxEntry[] = [];
    const unreadable: OutboxKey[] = [];
    for (const row of rows) {
      if (row.value?.owner !== this.#owner) continue;
      const parsed = parseCommand(row.value.command);
      if (parsed.ok) entries.push({ key: row.key, command: parsed.command });
      else unreadable.push(row.key);
    }
    return { entries, unreadable };
  }

  /** Remove entries by key. */
  async remove(keys: readonly OutboxKey[]): Promise<void> {
    await this.#store.remove(keys);
  }
}
